import { useState, useEffect } from 'react';

interface Breakpoints {
    isXs: boolean;
    isSm: boolean;
    isMd: boolean;
    isLg: boolean;
    isXl: boolean;
}

const breakpoints: Record<keyof Breakpoints, string> = {
    isXs: '(max-width: 639px)', // Extra Small: < 640px
    isSm: '(min-width: 640px) and (max-width: 767px)', // Small: 640px - 767px
    isMd: '(min-width: 768px) and (max-width: 1023px)', // Medium: 768px - 1023px
    isLg: '(min-width: 1024px) and (max-width: 1279px)', // Large: 1024px - 1279px
    isXl: '(min-width: 1280px)', // Extra Large: 1280px+
};

/**
 * Hook to detect current viewport breakpoint
 */
export const useBreakpoint = (): Breakpoints => {
    const getMatches = (): Breakpoints => {
        return Object.keys(breakpoints).reduce((acc, key) => {
            const typedKey = key as keyof Breakpoints;
            acc[typedKey] = window.matchMedia(breakpoints[typedKey]).matches;
            return acc;
        }, {} as Breakpoints);
    };

    const [matches, setMatches] = useState<Breakpoints>(getMatches);

    useEffect(() => {
        const handleResize = () => setMatches(getMatches());

        const mediaQueryLists = Object.values(breakpoints).map(query => window.matchMedia(query));
        mediaQueryLists.forEach(mql => mql.addEventListener('change', handleResize));

        handleResize();

        return () => {
            mediaQueryLists.forEach(mql => mql.removeEventListener('change', handleResize));
        };
    }, []);

    return matches;
};
